// "Add Hamyan to your home screen" — the messenger-native version.
//
// Bale, Eitaa and Telegram (8.0+) all expose the same pair: ask the client
// whether we are already installed, and trigger its native install prompt.
// Verified in each shipped SDK bundle.
//
// The whole value is in *not* asking the wrong people. `checkHomeScreenStatus`
// distinguishes "already added" from "could be added" from "this client has no
// such concept" (desktop/web), so the prompt only ever reaches someone it can
// actually work for.

import { resolvePlatform } from './detect'
import { loadSdk } from './sdk'

export type HomeScreenStatus = 'added' | 'missed' | 'unsupported' | 'unknown'

const DISMISSED_KEY = 'miniapp:home_screen_dismissed'
const FRESH_LOGIN_KEY = 'miniapp:fresh_login'
const CHECK_TIMEOUT_MS = 3000

/**
 * Mark that a session was just established, so the prompt shows on the first
 * launch after login rather than on some arbitrary later visit.
 *
 * sessionStorage: it should describe *this* launch and not linger.
 */
export const markFreshLogin = () => {
    try {
        window.sessionStorage.setItem(FRESH_LOGIN_KEY, '1')
    } catch {
        /* best effort */
    }
}

export const consumeFreshLogin = (): boolean => {
    try {
        const fresh = window.sessionStorage.getItem(FRESH_LOGIN_KEY) === '1'
        // One-shot: re-rendering the home screen must not re-trigger it.
        if (fresh) window.sessionStorage.removeItem(FRESH_LOGIN_KEY)
        return fresh
    } catch {
        return false
    }
}

/**
 * Declining is remembered in localStorage, not sessionStorage — "no" should
 * survive closing the app. Asking again on every launch is how a helpful
 * prompt turns into a reason to uninstall.
 */
export const rememberDismissed = () => {
    try {
        window.localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
        /* best effort */
    }
}

export const wasDismissed = (): boolean => {
    try {
        return window.localStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
        return false
    }
}

/**
 * Ask the client whether we are on the home screen already.
 *
 * Resolves to 'unknown' if the SDK is missing or the client never answers —
 * both mean "don't prompt", which is the safe direction.
 */
export const checkHomeScreenStatus = async (): Promise<HomeScreenStatus> => {
    const platform = resolvePlatform()
    if (!platform) return 'unknown'

    const webApp = await loadSdk(platform)
    const check = webApp?.checkHomeScreenStatus
    if (!webApp || !check) return 'unsupported'

    return new Promise<HomeScreenStatus>((resolve) => {
        let settled = false
        const finish = (status: HomeScreenStatus) => {
            if (settled) return
            settled = true
            resolve(status)
        }
        try {
            check.call(webApp, (result) =>
                finish((result?.status as HomeScreenStatus) || 'unknown')
            )
        } catch {
            finish('unsupported')
        }
        // An older client may accept the call and never answer.
        window.setTimeout(() => finish('unknown'), CHECK_TIMEOUT_MS)
    })
}

/** Trigger the client's own add-to-home-screen prompt. */
export const addToHomeScreen = async (): Promise<void> => {
    const platform = resolvePlatform()
    if (!platform) return
    const webApp = await loadSdk(platform)
    try {
        webApp?.addToHomeScreen?.()
    } catch {
        /* nothing we can do; the client owns this UI */
    }
}

/**
 * Should we show our own "add us" nudge right now?
 *
 * Only when: we are in a mini app, the user just logged in, they have not
 * already said no, and the client reports we are installable but not installed.
 */
export const shouldOfferHomeScreen = async (): Promise<boolean> => {
    if (wasDismissed()) return false
    if (!consumeFreshLogin()) return false
    return (await checkHomeScreenStatus()) === 'missed'
}
